// {{{ PopupMenu

/**
 * Popup menu for use with PopupManager
 */
GW.define('PopupMenu', 'GW.Component', {
	/**
	 * Menu options:
	 *   - iconName (use a new style SVG symbol icon)
	 *   - text
	 *   - cls
	 *   - handle
	 */
	options: [],

	initComponent() {
		let hasIcons = this.options.some(o => !o.hidden && (o.iconName || o.rightIconName));

		this.el = document.gwCreateElement({
			className: 'popup-menu popup-arrow' + (hasIcons ? '' : ' no-icons'),
			tabIndex: '0',
			'on*:click': ev => {
				let optionEl = ev.target.closest('.option');
				if (optionEl) {
					this.onOptionClick(optionEl, ev);
				}
			},
		}, this);

		this.el.gwReplaceChildren(this.renderMenu(), this);

		// gate this on mobile, because it causes stutter mid-animation
		if (!Application.isMobileUI()) {
			Utils.timer(this, 'focus', 100, () => {
				this.focus();
			});
		}
	},

	focus() {
		this.el.focus();
	},

	setOptions(opts) {
		this.options = opts;

		this.el.gwReplaceChildren(this.renderMenu());
	},

	renderOption(option) {
		if (option.render) {
			return option.render;
		}

		return {
			$option: option,
			nodeName: 'a',
			href: option.href || '#',
			target: option.href ? '_blank' : undefined,
			className: 'option ' + (option.cls || ''),
			children: [{
				className: 'option-icon',
				children: [option.iconName ? Utils.useIcon(option.iconName) : null]
			}, {
				className: 'option-text',
				textContent: option.text,
			}, {
				':skip': !option.rightIconName,
				className: 'option-icon-right',
				children: [option.rightIconName ? Utils.useIcon(option.rightIconName) : null]
			}, {
				':skip': !option.submenu,
				className: 'option-submenu',
				children: [Utils.useIcon('arrow-simple-right')],
			}],
		};
	},

	renderMenu() {
		return this.options
			.filter(o => o && !o.hidden)
			.map(o => this.renderOption(o));
	},

	onOptionClick(option, ev) {
		let o = option.$option;
		if (o.submenu) {
			let popup = new PopupMenu({
				options: o.submenu,
				'after:onOptionClick': () => {
					PopupManager.hide(this);
				},
			});

			PopupManager.show(popup, {
				anchorEl: option,
				forceAxis: 'horizontal',
				preferReadingOrder: true,
			});
			return;
		}

		if (o.handle) {
			ev.preventDefault();
			o.handle();
		}

		PopupManager.hide(this);
	},

	$key_ArrowUp(ev) {
		this.focusPrev();
	},

	$key_ArrowDown(ev) {
		this.focusNext();
	},

	$key_Tab(ev) {
		if (ev.shiftKey) {
			this.focusPrev();
		} else {
			this.focusNext();
		}

		ev.preventDefault();
	},

	$key_Escape(ev) {
		PopupManager.hide(this);
	},

	focusNext() {
		let opts = Array.from(this.el.querySelectorAll('.option'));
		let idx = opts.findIndex(el => el.matches(':focus'));
		let next = opts[idx + 1] || opts[opts.length - 1];

		next && next.focus();
	},

	focusPrev() {
		let opts = Array.from(this.el.querySelectorAll('.option'));
		let idx = opts.findIndex(el => el.matches(':focus'));
		let next = opts[idx - 1] || opts[0];

		next && next.focus();
	}
});

// }}}

// Tables:

// {{{ SmartTable

GW.define('SmartTable', 'GW.Component', {
	selectable: false,
	emptyText: _('Žádné výsledky'),
	sort: null,
	limit: null,
	limitMessage: _('Zobrazuje se {n} z {total} záznamů. Zobrazení dalších záznamů může trvat dlouhou dobu a výrazně zpomalit prohlížeč.'),

	getColumns() {
		return [];
	},

	'after:initComponent'() {
		this.el = document.gwCreateElement({
			className: 'smart-table',
			children: [{
				nodeName: 'table',
				children: [{
					nodeName: 'thead',
					ref: 'head',
					children: [{
						ref: 'headRow',
						nodeName: 'tr',
					}],
				}, {
					nodeName: 'tbody',
					ref: 'body',
					'on:click': ev => {
						let tr = ev.target.closest('tr');
						if (tr && tr.$data) {
							this.onRowClick(tr.$data, tr, ev);
						}
					},
				}, {
					nodeName: 'tfoot',
					ref: 'footer',
				}],
			}, {
				className: 'empty-message',
				textContent: this.emptyText,
				ref: 'msgEl',
			}]
		}, this);

		this.el.classList.toggle('selectable', !!this.selectable);
		this.renderHeaderRow(!this.rows || !this.rows.length);

		if (this.extraClass) {
			this.el.classList.add(...this.extraClass.split(/\s+/));
		}

		if (this.rows) {
			this.setData(this.rows);
		}

		if (this.monitorOverflow) {
			this.initOverflowMonitor();
		}
	},

	'before:destroy'() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}
	},

	updateOverflowed() {
		if (this.monitorOverflow) {
			this.el.dataset.overflowed = this.el.clientWidth < this.el.scrollWidth ? '1' : '0';
		}
	},

	initOverflowMonitor() {
		if (this.resizeObserver) {
			return;
		}

		this.resizeObserver = new ResizeObserver(entries => {
			this.updateOverflowed();
		});

		this.resizeObserver.observe(this.el);
		this.updateOverflowed();
	},

	renderHeaderRow(empty) {
		const that = this;
		this.headRow.classList.toggle('no-data', empty);
		this.headRow.gwReplaceChildren(this.getColumns().map(c => {
			let filterIcon = c.filter ? Utils.useIcon( this.isFilterActive(c) ? 'filled-true' : 'filled-false', {cls: 'filter-icon'} ) : null;

			let sortIcon = null;
			if (c.sortable) {
				if (this.sort && this.sort.substring(1) === c.id) {
					sortIcon = Utils.useIcon(this.sort[0] === '<' ? 'sort-asc' : 'sort-desc', {cls: 'sort-icon'})
				} else {
					sortIcon = Utils.useIcon('sort', {cls: 'sort-icon'});
				}
			}

			return {
				$col: c,
				nodeName: 'th',
				className: (c.align || '') + (this.isFilterActive(c) ? ' filtered' : '') + (c.sortable ? ' sortable' : '') + (filterIcon ? ' filterable' : ''),
				children: [
					sortIcon ? {
						className: 'header-sort',
						children: [
							sortIcon, 
							c.name || ''
						],
						'on:click'(e) {
							let newSort = '<' + c.id;
							if (newSort === that.sort) {
								newSort = '>' + c.id;
							}
							if (that.sort === '>' + c.id) {
								newSort = null;
							}
							that.onSortChange(newSort);
						},
					} : (c.name || ''),
					filterIcon ? {
						className: 'header-filter',
						children: [filterIcon],
						'on:click'(e) {
							that.onFilterSelect(e.target, c);
						},
					} : null,
				],
				'attr:data-col': c.id,
			};
		}), this);
	},

	setSort(sort) {
		this.sort = sort;
	},

	renderCell(td, r, c) {
		if (c.format && Formats[c.format]) {
			td.textContent = Formats[c.format](r[c.id]);
		} else if (c.formatCell) {
			c.formatCell(td, r[c.id], r);
		} else {
			td.textContent = r[c.id];
		}

		if (c.id === "ctl" && Application.isMobileUI()) {
			td.tabIndex = 0;
		}
	},

	renderLimitMessage() {
		let columns = this.getColumns();

		let tr = this.footer.gwCreateChild({
			nodeName: 'tr',
			className: 'limit-message',
			children: [{
				nodeName: 'td',
				colSpan: columns.length,
				children: [this.limitMessage.replace('{n}', this.limit).replace('{total}', this.rows.length), ' ', {
					':skip': !this.onDisplayRestClick,
					nodeName: 'a',
					href: '#',
					textContent: _('zobrazit zbytek'),
					'on:click': ev => {
						ev.preventDefault();
						tr.remove();
						this.onDisplayRestClick();
					},
				}],
			}],
		});
	},

	onDisplayRestClick() {
		this.renderRows(this.rows.slice(this.limit));
	},

	setData(rows) {
		this.rows = rows;

		this.renderHeaderRow(rows.length <= 0);

		this.body.textContent = '';
		this.footer.textContent = '';
		this.msgEl.classList.toggle('hidden', rows.length > 0);

		if (this.limit != null) {
			this.renderRows(rows.slice(0, this.limit));

			if (rows.length > this.limit) {
				this.renderLimitMessage();
			}
		} else {
			this.renderRows(rows);
		}

		this.updateOverflowed();
	},

	renderRows(rows) {
		let columns = this.getColumns();

		for (let r of rows) {
			let tr = this.body.gwCreateChild({
				$data: r,
				nodeName: 'tr',
				children: columns.map(c => {
					let td = document.gwCreateElement({
						nodeName: 'td',
						'attr:data-col': c.id,
						className: [c.filter && 'filterable', c.align].filter(c => c).join(' '),
					});

					this.renderCell(td, r, c);

					return td;
				}),
			});

			this.renderRow(tr, r);
		}
	},

	renderRow(tr, row) {
	},

	onSortChange(sort) {
	},

	onFilterSelect(el, column) {
	},
	
	isFilterActive(column) {
		return false;
	},

	onRowClick(row, tr, ev) {
	},

	selectRow(fn) {
		this.body.querySelectorAll('tr').forEach(tr => tr.classList.toggle('selected', !!fn(tr.$data, tr)));
	},

	refresh() {
		this.setData(this.rows);
	},
});

// }}}
// {{{ SmartTable.Filter

GW.define('SmartTableFilter', 'GW.Component', {

	initComponent() {
		this.el = document.gwCreateElement({
			className: 'popup-menu',
			children: [{
				ref: 'contentEl',
			}, {
				className: 'filter-buttons',
				children: [{
					nodeName: 'button',
					className: 'secondary small',
					textContent: _('Zrušit filtry'),
					'on:click': () => {
						this.fire('filterChanged', null);
						this.resetState();
						this.popupHide();
					},
				}, {
					nodeName: 'button',
					className: 'primary small',
					textContent: _('Hotovo'),
					'on:click': () => {
						let value = this.getValue();
						if (JSON.stringify(value) === JSON.stringify(this.getDefaultValue())) {
							value = null;
						}
						this.fire('filterChanged', value);
						this.popupHide();
					},
				}]
			}]
		}, this);
	},
	
	popupShow(th) {
		this.el.classList.remove('hidden');
		PopupManager.show(this, {
			anchorEl: th,
			forceAxis: 'vertical',
			preferReadingOrder: true,
		});
	},

	popupHide() {
		PopupManager.hide(this);
	},
	
	hidePopup() {
		// called in PopupManager.hide()
		this.el.classList.add('hidden');
	},

	setValue(value) {},
	
	resetState() {},

	getValue() {},
});

// }}}
// {{{ Filters

const Filters = {
	'options': {
		matches: (data_val, filter_val) => filter_val.includes(data_val),
		
		render: (data, filter, column) => {
			/**
			 * return formatted value using SmartTable.renderCell() and simulating <td> element to pass to it
			 * @TODO avoid creating temporary <td> and "row" value using {[id]: val}, refactor SmartTable.renderCell()?
			 */
			const formatVal = val => {
				let td = document.gwCreateElement({
					nodeName: 'td',
				});

				SmartTable.prototype.renderCell(td, {[column.id]: val}, column);

				return td.textContent;
			};
			
			const options = filter.options || [...new Set(data)].sort().map(val => ({
				value: val,
				title: formatVal(val),
			}));
			
			const defaultValue = options.map(option => option.value);
			
			return new SmartTableFilter({
				options,
			
				value: defaultValue,
			
				getDefaultValue() {
					return defaultValue;
				},
			
				resetState() {
					if (!this.inputSearch) {
						// search is not enabled, no need to do anything
						return;
					}

					this.inputSearch.value = '';
					this.updateCheckboxes(); // update all checkboxes including its state
				},

				setValue(value) {
					this.value = value || this.getDefaultValue();
					this.updateCheckboxesState(); // update only state
				},

				getValue() {
					return this.getCheckboxes(true).map(el => el.$value);
				},
			
				'after:initComponent'() {
					const id = GW.uniqueId('input-');

					this.contentEl.classList.add('filter-options');
					this.contentEl.gwReplaceChildren([{
						':skip': !filter.showSearch,
						ref: 'inputSearch',
						nodeName: 'input',
						type: 'search',
						className: 'small',
						placeholder: _('Hledat'),
						'on:input': ev => {
							this.updateCheckboxes();
						},
					},{
						className: 'check check-all',
						children: [{
							className: 'filter-check-all small',
							nodeName: 'input',
							type: 'checkbox',
							ref: 'checkboxAll',
							id,
							'on:input': ev => {
								this.getCheckboxes().forEach(el => el.checked = ev.target.checked);
							},
						}, {
							nodeName: 'label',
							htmlFor: id,
							children: [_('Vybrat vše')],
						}],
					}, {
						ref: 'checkboxes',
						className: 'filter-options-items',
					}], this);

					this.updateCheckboxes();

					if (filter.showSearch) {
						setTimeout(() => this.inputSearch.focus(), 0);
					}
				},

				getCheckboxes(onlyChecked) {
					let selector = 'input[type=checkbox]:not(.filter-check-all)';
					if (onlyChecked) {
						selector += ':checked';
					}

					return [...this.el.querySelectorAll(selector)];
				},
			
				updateCheckboxesState() {
					for (let input of this.getCheckboxes()) {
						input.checked = this.value.includes(input.$value);
					}

					this.checkboxAll.checked = this.getCheckboxes().every(el => el.checked);
				},
			
				updateCheckboxes() {
					const match = this.inputSearch ? TextUtils.createMatchAll(this.inputSearch.value) : () => true;

					this.checkboxes.gwReplaceChildren(this.options.map((option, i) => {
						const id = GW.uniqueId('input-');

						return {
							':skip': !match(option.title),
							className: 'check',
							children: [{
								nodeName: 'input',
								className: 'small',
								type: 'checkbox',
								$value: option.value,
								id,
								'on:input': e => {
									this.checkboxAll.checked = this.getCheckboxes().every(el => el.checked);
								},
							}, {
								nodeName: 'label',
								htmlFor: id,
								textContent: option.title,
							}],
						};
					}));

					this.updateCheckboxesState();
				},
			});
		},
	},
	
	'duration': {
		matches: (data_val, [from, to]) => data_val >= from && data_val <= to,
		
		render(data, filter) {
			const durations = data;
			const min = Math.min(...durations);
			const max = Math.max(...durations);

			return new SmartTableFilter({
				min,
				max,
				
				getDefaultValue() {
					return [this.min, this.max];
				},

				setValue(value) {
					value = value || this.getDefaultValue();
					let [from, to] = value;
					from /= 1000;
					to /= 1000;
					this.fromMinutes.value = Math.floor(from / 60);
					this.fromSeconds.value = Math.floor(from % 60);
					this.toMinutes.value = Math.floor(to / 60);
					this.toSeconds.value = Math.floor(to % 60);
				},

				getValue() {
					return [
						(this.fromMinutes.value * 60 * 1000) + (this.fromSeconds.value * 1000),
						(this.toMinutes.value * 60 * 1000) + (this.toSeconds.value * 1000),	
					]
				},

				'after:initComponent'() {
					const selectAllOnClick = ev => ev.target.select();

					this.contentEl.classList.add('filter-duration');
					this.contentEl.gwReplaceChildren([{
						className: 'filter-duration-item',
						children: [{
							textContent: _('Od'),
							className: 'label',
						}, {
							className: 'input-group',
							children: [{
								className: 'input-label-inset',
								children: [{
									nodeName: 'input',
									className: 'small',
									type: 'number',
									ref: 'fromMinutes',
									'on:click': selectAllOnClick,
								}, {
									nodeName: 'label',
									textContent: _('Minut')
								}]
							}, {
								className: 'input-label-inset',
								children: [{
									nodeName: 'input',
									className: 'small',
									type: 'number',
									ref: 'fromSeconds',
									'on:click': selectAllOnClick,
								}, {
									nodeName: 'label',
									textContent: _('Sekund')
								}]
							}],
						}],
					}, {
						className: 'filter-duration-item',
						children: [{
							textContent: _('Do'),
							className: 'label',
						}, {
							className: 'input-group',
							children: [{
								className: 'input-label-inset',
								children: [{
									nodeName: 'input',
									className: 'small',
									type: 'number',
									ref: 'toMinutes',
									'on:click': selectAllOnClick,
								}, {
									nodeName: 'label',
									textContent: _('Minut')
								}]
							}, {
								className: 'input-label-inset',
								children: [{
									nodeName: 'input',
									className: 'small',
									type: 'number',
									ref: 'toSeconds',
									'on:click': selectAllOnClick,
								}, {
									nodeName: 'label',
									textContent: _('Sekund')
								}]
							}],
						}],
					}], this);

					setTimeout(() => this.fromMinutes.select(), 0);
				},
			});
		},
	},
}

// }}}
// {{{ FilterableSmartTable

GW.define('FilterableSmartTable', 'SmartTable', {

	data: null, // non filtered/sorted dataset

	filter: {}, // current active filters with its value(s)

	filterEls: {}, // cached GW components to save its state if shown again

	onFilterSelect(el, column) {
		const gwFilter = this.renderFilter(column);
		if (gwFilter) {
			gwFilter.popupShow(el);
		}
	},
	
	onSortChange(sort) {
		this.setSort(sort);
		this.updateData();
	},

	isFilterActive(column) {
		return !!this.filter[column.id];
	},

	setData(data) {
		this.data = data;
		this.resetFilters();
		this.updateData();
	},

	updateData() {
		let data = (this.data || []).filter(this.applyFilter.bind(this));
		
		if (this.sort) {
			let dir = this.sort[0];
			let column_id = this.sort.substring(1);
			let column = this.getColumns().find(c => c.id === column_id);
			if (!column) {
				SmartTable.prototype.setData.call(this, data);
				return;
			}

			let sortable = column.sortable;
			if (typeof sortable != 'object' || !sortable) {
				sortable = {
					type: 'string',
				};
			}

			/*
			 * Prepare values by which we'll be sorting.
			 */
			let valMap = new WeakMap();
			if (sortable.value instanceof Function) {
				for (let row of data) {
					valMap.set(row, sortable.value(row[column_id], row));
				}
			} else if (sortable.value == 'celltext') {
				let tmp = document.createElement('div');
				for (let row of data) {
					tmp.textContent = '';
					this.renderCell(tmp, row, column);

					valMap.set(row, tmp.innerText);
				}
			} else {
				for (let row of data) {
					valMap.set(row, row[column_id]);
				}
			}

			/*
			 * Sort by the weakmap values.
			 */
			data.sort((row1, row2) => {
				let val1 = valMap.get(row1);
				let val2 = valMap.get(row2);
				let sign = (dir === '<' ? 1 : -1) * (sortable.revert ? -1 : 1);

				if (sortable.type === 'numeric') {
					return sign * (val1 - val2);
				} else { // default type is 'string'
					return sign * (val1 || '').localeCompare(val2 || '');
				}
			});
		}

		SmartTable.prototype.setData.call(this, data);
	},

	filterMatches(row, c, filter) {
		if (this.filter[c.id] && Filters.hasOwnProperty(filter.type)) {
			// use defined 'matches' function or the filter default 'matches' function
			const matches = filter.matches || Filters[filter.type].matches; 

			return matches(row[c.id], this.filter[c.id], row);
		}

		return true;
	},

	applyFilter(row) {
		for (let c of this.getColumns()) {
			if (c.filter) {
				if (!this.filterMatches(row, c, c.filter)) {
					return false;
				}
			}
		}
		return true;
	},

	renderFilter(column) {
		if (!this.data) {
			// no data, filters dependent on the data (currently both of them) are not able to render without it
			return null;
		}
		
		const filter = column.filter;
		
		let tableFilter;

		if (!this.filterEls[column.id] && Filters.hasOwnProperty(filter.type)) {
			const data = this.data.map(r => r[column.id]);

			// TODO remove third argument 'column' the filter should not need that
			this.filterEls[column.id] = Filters[filter.type].render(data, filter, column);

			this.filterEls[column.id].on('filterChanged', (c, value) => {
				this.filter[column.id] = value;
				this.updateData();
			});
		}

		tableFilter = this.filterEls[column.id];

		if (tableFilter) {
			tableFilter.setValue(this.filter[column.id]);
		}

		return tableFilter;
	},

	resetFilters() {
		for (let id in this.filterEls) {
			this.filterEls[id].destroy();
			delete this.filterEls[id];
		}

		for (let id in this.filter) {
			delete this.filter[id];
		}		
	},
	
	releaseComponent() {
		this.resetFilters();
	},

	refresh() {
		this.updateData();
	},
});

// }}}
// {{{ ExpandableSmartTable

GW.define('ExpandableSmartTable', 'SmartTable', {

	data: null,
	
	allGroups: new Set(), // cached group names of all rows
	
	expanded: new Set(), // list of expanded groups
	
	getGroupColumn() {
		// group by first column by default
		return this.getColumns()[0].id;
	},
	
	setData(data, expanded) {
		const groupColumn = this.getGroupColumn();
		
		let lastGroup;
		this.data = data;
		this.data.forEach(row => {
			const group = row[groupColumn];
			const first = group !== lastGroup;
			row._expandable = {
				group, 
				topLevel: first
			};
			lastGroup = group;
		});
		
		this.allGroups = new Set(this.data.map(r => r._expandable.group));

		// set data and expanded, then call updateData() to show correct expanded groups of new data
		this.expanded = expanded ? new Set([...expanded].filter(e => this.allGroups.has(e))) : new Set();
		
		this.updateData();
		this.fire('expand', this.expanded);
	},

	updateData() {
		const data = this.data.filter(row => {
			const {_expandable} = row;
			return !_expandable.group || _expandable.topLevel || this.expanded.has(_expandable.group);
		});
		SmartTable.prototype.setData.call(this, data);
	},

	onRowClick(row, tr, ev) {
		const {_expandable} = row;
		const {topLevel, group} = _expandable;
		if (topLevel) {
			this.toggleGroup(group);
		}
	},
	
	toggleGroup(group) {
		if (this.expanded.has(group)) {
			this.expanded.delete(group);
		} else {
			this.expanded.add(group);
		}
		this.updateData();
		this.fire('expand', this.expanded);
	},

	toggleAllGroups() {
		if (this.allExpanded()) {
			this.expanded.clear();
		} else {
			this.allGroups.forEach(g => this.expanded.add(g));
		}
		this.updateData();
		this.fire('expand', this.expanded);
	},
	
	allExpanded() {
		return this.allGroups.size > 0 && [...this.allGroups].every(g => this.expanded.has(g));	
	},

	'after:renderRow'(tr, row) {
		const {_expandable} = row;
		const expanded = this.expanded.has(_expandable.group);

		// prepend column with expandable icon
		tr.insertBefore(document.gwCreateElement({
			nodeName: 'td',
			className: 'expandable-icon',
			children: _expandable.group && _expandable.topLevel ?
				[Utils.useIcon(expanded ? 'arrow-simple-down' : 'arrow-simple-right')] : [],
		}), tr.firstChild);
		
		if (!_expandable.group) {
			return;
		}
		
		tr.classList.toggle('expandable', _expandable.topLevel);
		if (_expandable.topLevel) {
			tr.classList.toggle('expandable-expanded', expanded);
		}
	},
	
	'after:renderHeaderRow'(empty) {
		if (empty) {
			return;
		}
		this.headRow.insertBefore(document.gwCreateElement({
			'nodeName': 'th',
			'className': 'expandable-header',
		}), this.headRow.firstChild);
	},

	refresh() {
		this.updateData();
	},
});

// }}}
// {{{ PropertyTable

GW.define('PropertyTable', 'GW.Component', {
	properties: [],

	getProperties() {
		return this.properties;
	},

	'after:initComponent'() {
		this.el = document.gwCreateElement({
			className: 'property-table',
		}, this);

		if (this.value) {
			this.setValue(this.value);
		}
	},

	renderProperty(ct, def, data) {
		if (def.format && Formats[def.format] && data[def.id] != null) {
			ct.textContent = Formats[def.format](data[def.id]);
		} else if (def.formatCell) {
			return def.formatCell(ct, data[def.id], data);
		} else if (data[def.id] != null) {
			ct.textContent = data[def.id];
		} else {
			return false;
		}
	},

	setValue(data) {
		this.el.textContent = '';

		for (let def of this.getProperties()) {
			let ct = document.gwCreateElement({className: 'value'});
			if (this.renderProperty(ct, def, data) === false) {
				continue;
			}

			this.el.gwCreateChild({
				className: 'property',
				'attr:data-name': def.id,
				children: [{
					className: 'label',
					textContent: def.name,
				}, ct],
			});
		}
	},
});

// }}}

// Dialogs:

// {{{ Main.Dialog

GW.define('Main.Dialog', 'GW.Component', {
	title: '',
	subtitle: '',
	allowCloseButton: true,
	parentElement: null, // parent element to show the dialog in (if not set, the default is document.body)

	initComponent() {
		this.el = document.gwCreateElement({
			className: 'admin-dialog-mask',
			tabIndex: '-1',
			$key_Escape: () => {
				this.close();
			},
			children: [{
				ref: 'dlgEl',
				className: 'admin-dialog',
				...this.getExtraDailogProps(),
				children: [{
					ref: 'headerEl',
					className: 'header',
					children: [{
						ref: 'titleEl',
						nodeName: 'h1',
						textContent: this.title,
					}, {
						ref: 'headerCenterCt',
						className: 'center',
					}, {
						':skip': !this.allowCloseButton,
						className: 'close',
						children: [Utils.useIcon('close')],
						'on:click': ev => {
							this.close();
						},
					}],
				}, {
					':skip': !this.subtitle,
					className: 'subtitle',
					textContent: this.subtitle,
				}, {
					ref: 'bodyEl',
					className: 'body',
				}, {
					ref: 'footerEl',
					className: 'footer',
				}],
			}],
		}, this);

		PopupManager.show(this, {
			noLayout: true,
			ignoreScroll: true,
			parentElement: this.parentElement,
		});

		this.focus();
	},

	getExtraDailogProps() {
		return {};
	},

	focus() {
		this.el.focus();
	},
	
	close() {
		PopupManager.hideDownTo(this);
		PopupManager.hide(this);
	},

	getClosePromise() {
		return this.closePromise || (this.closePromise = new Promise(res => {
			this.on('destroy', c => res());
		}));
	},
});

// }}}
// {{{ Main.FormDialog

GW.define('Main.FormDialog', 'Main.Dialog', {
	title: '',
	subtitle: '',
	confirmText: '',
	cancelText: '',
	disableSubmitOnInvalidForm: false,

	getExtraDailogProps() {
		return {
			nodeName: 'form',
			noValidate: true,
			'on:submit': ev => {
				ev.preventDefault();
				this.onSubmit();
			},
		};
	},

	'after:initComponent'() {
		this.el.classList.add('form');

		this.bodyEl.gwCreateChild({
			children: this.renderFormFields().concat({
				className: 'form-buttons',
				children: this.renderFormButtons(),
			}),
		}, this);

		this.focus();

		this.on('change', c => {
			this.refreshVisibility();
		}, null, true);

		this.refreshVisibility();
	},

	renderFormButtons() {
		return [{
			':skip': !this.allowCloseButton,
			nodeName: 'button',
			type: 'button',
			className: 'secondary',
			textContent: this.cancelText || _('Zavřít'),
			'on:click': ev => {
				ev.preventDefault();
				this.destroy();
			},
		}, {
			ref: 'buttonConfirm',
			nodeName: 'button',
			type: 'submit',
			className: 'primary',
			textContent: this.confirmText || _('Potvrdit'),
		}];
	},

	renderFormFields() {
		return [];
	},

	refreshVisibility() {
		/*
		 * Itereate several times until visibility stops changing.
		 * This is necessary to handle nested rules, when visibility
		 * of one component affects another component.
		 */
		for (let i = 0; i < 8; i++) {
			let data = this.getFormFieldsData();
			let changed = false;
			/*
			 * We need to handle nested FormFieldsGroup specially.
			 * FormFieldsGroup is reusable grouping of form components,
			 * that has its own value which is represented as a JS
			 * object with keys matching all nested form components.
			 *
			 * We want to pass only the value of the
			 * FormFieldsGroup's data to visibleWhen to make re-use
			 * possible and simple.
			 */
			let refreshComponent = (parent, data) => {
				parent.traverseChildren(f => {
					if (f.visibleWhen) {
						f.el.gwHide(!f.visibleWhen(data));

						if (f.setDisabled) {
							let wasDisabled = f.isDisabled();
							let disable = !f.visibleWhen(data);

							f.setDisabled(disable);

							if (wasDisabled !== disable) {
								changed = true;
							}
						}
					}

					if (this.isFormFieldGroupComponent(f)) {
						refreshComponent(f, f.getValue());
						return 'skip';
					}
				});
			};

			refreshComponent(this, data);

			if (!changed) {
				break;
			}
		}

		if (this.buttonConfirm && this.disableSubmitOnInvalidForm) {
			let valid = this.validateForm(false);

			this.buttonConfirm.disabled = !valid;
		}
	},

	focus() {
		for (let f of this.el.querySelectorAll('input,select,textarea,[tabindex]:not([tabindex="-1"]')) {
			if (!f.disabled) {
				f.focus();
				break;
			}
		}
	},

	async commitFormFields() {
                let fns = [];

		this.traverseChildren(function(f) {
			if (f.name && f.getValue && f.commit) {
				fns.push(() => {
					return f.commit();
				});

				return 'skip';
			}

			return null;
		});

		for (let fn of fns) {
			await fn();
		}
	},

	async onSubmit() {
		if (!this.reportFormValid()) {
			return;
		}

		let restore = this.setFormDisabled();

		try {
			await this.commitFormFields();

			let data = this.getFormFieldsData();

			this.fire('aftersave', await this.onSave(data));

			this.destroy();
		} catch(ex) {
			restore();
			throw ex;
		}
	},

	onSave(data) {
	},

	...FormMixin,

	setFormFieldsData(data) {
		FormMixin.setFormFieldsData.call(this, data);
		this.refreshVisibility();
	},
});

// }}}
// {{{ Main.ConfirmDialog

GW.define('Main.ConfirmDialog', 'Main.Dialog', {
	title: '',
	subtitle: '',

	confirmText: _('Ano'),
	cancelText: _('Ne'),

	allowCloseButton: false,

	getExtraDailogProps() {
		return {
			nodeName: 'form',
			noValidate: true,
			'on:submit': ev => {
				ev.preventDefault();
				this.onSubmit();
			},
		};
	},

	'after:initComponent'() {
		this.el.classList.add('form');

		this.bodyEl.gwCreateChild({
			className: 'form-buttons',
			children: this.renderFormButtons(),
		});

		this.focus();
	},

	renderFormButtons() {
		return [{
			nodeName: 'button',
			type: 'button',
			className: this.cancelCls || 'secondary',
			textContent: this.cancelText,
			'on:click': ev => {
				ev.preventDefault();
				this.destroy();
			},
		}, {
			nodeName: 'button',
			type: 'submit',
			className: this.confirmCls || 'primary',
			textContent: this.confirmText,
		}];
	},

	focus() {
		for (let f of this.el.querySelectorAll('button')) {
			if (!f.disabled) {
				f.focus();
			}
		}
	},

	async onSubmit() {
		if (!this.reportFormValid()) {
			return;
		}

		try {
			this.el.querySelectorAll('button').forEach(b => b.disabled = true);

			this.fire('aftersave', await this.onSave());

			this.destroy();
		} catch(ex) {
			this.el.querySelectorAll('button').forEach(b => b.disabled = false);
			throw ex;
		}
	},

	onSave() {
	},

	...FormMixin,
});

// }}}
// {{{ Main.NotesDialog

GW.define('Main.NotesDialog', 'Main.Dialog', {
	title: _('Poznámky'),

	'after:initComponent'() {
		this.bodyEl.gwReplaceChildren([{
			className: 'comment-box',
			children: [{
				nodeName: 'label',
				textContent: _('Nová poznámka'),
			}, {
				ref: 'noteField',
				xtype: 'TextareaField',
				'on:change': () => {
					this.fire('interact')
					this.sendButton.disabled = !this.noteField.getValue();
				},
				$key_Ctrl_Enter: ev => {
					this.saveNote();
					this.close();
				},
			}, {
				className: 'buttons',
				children: [{
					ref: 'sendButton',
					nodeName: 'button',
					type: 'button',
					className: 'primary',
					disabled: true,
					textContent: _('Uložit a zavřít'),
					'on:click': ev => {
						this.saveNote();
						this.close();
					},
				}],
			}],
		}, {
			ref: 'notes',
			className: 'notes-list',
		}], this);
		this.reload();
	},

	renderNotes(list) {
		this.notes.gwReplaceChildren(list.map(n => {
			return {
				className: 'note',
				children: [{
					className: 'header',
					children: [{
						className: 'author',
						textContent: n.user,
					}, {
						className: 'time',
						textContent: Formats.datetime(n.ts),
					}]
				}, {
					className: 'body',
					textContent: n.text,
				}]
			};
		}));
	},

	async saveNote() {
		let text = this.noteField.getValue();
		if (text) {
			try {
				this.sendButton.disabled = true;
				this.noteField.setDisabled(true);

				await this.onSave(text);

				this.noteField.setValue();
				this.reload();
			} finally {
				this.noteField.setDisabled(false);
				this.sendButton.disabled = false;
			}
		}
	},

	async reload() {
		let signal = Utils.abortAndRetry(this, 'reload');

		this.renderNotes(await this.loadData(signal));
	},

	onSave(text) {
	},

	loadData(signal) {
		return [];
	},
});

// }}}
// {{{ Main.ListSelectDialog

GW.define('Main.ListSelectDialog', 'Main.FormDialog', {
	title: _('Vyberte z voleb'),
	selectAllText: _('Vybrat vše'),
	unselectAllText: _('Zrušit výběr'),
	items: [],
	reorderAllowed: true,

	'after:initComponent'() {
		this.bodyEl.classList.add('list-select-dialog');
		this.setItems(this.items);

		this.bodyEl.addEventListener('input', ev => {
			if (ev.target.closest('input')) {
				this.refreshSelectionButtons();
			}
		}, true);

		this.dnd = this.reorderAllowed && DnDListSetup(this.itemsCt, {
			startFilterClass: 'draggable-item',
			itemClass: 'draggable-item',
			onDragEnd: this.onDragEnd.bind(this)
		});
	},
	
	onDragEnd() {
		
	},

	'before:releaseComponent'() {
		this.dnd && this.dnd.destroy();
	},

	refreshSelectionButtons() {
		let selected = 0, unselected = 0;

		for (let cb of this.getCheckboxes()) {
			if (cb.getValue()) {
				selected++;
			} else {
				unselected++;
			}
		}

		this.selectionButtonsCt.gwReplaceChildren([{
			nodeName: 'li',
			children: [{
				className: 'form-field small',
				children: [{
					className: 'input-ct',
					children: [{
						className: 'ack-checkbox',
						children: [{
							className: 'check',
							children: [{
								nodeName: 'input',
								type: 'checkbox',
								className: 'small',
								checked: !!selected,
								indeterminate: !!(selected && unselected),
								'on:input': () => {
									if (selected) {
										this.unselectAll();
									} else {
										this.selectAll();
									}
								},
								id: 'list-select-dialog-all',
							}, {
								nodeName: 'label',
								htmlFor: 'list-select-dialog-all',
								textContent: selected ? this.unselectAllText : this.selectAllText,
							}],
						}]
					}]
				}],
			}],
		}]);
	},

	selectAll() {
		this.getCheckboxes().forEach(cb => cb.setValue(true));
		this.refreshSelectionButtons();
	},

	unselectAll() {
		this.getCheckboxes().forEach(cb => cb.setValue(false));
		this.refreshSelectionButtons();
	},

	renderFormFields() {
		return [{
			ref: 'selectionButtonsCt',
			nodeName: 'ul',
			className: 'items-list',
		}, {
			ref: 'itemsCt',
			nodeName: 'ul',
			className: 'items-list',
		}];
	},

	renderItems(items) {
		this.itemsCt.gwReplaceChildren(items.map(i => {
			return {
				nodeName: 'li',
				className: this.reorderAllowed ? 'draggable-item' : '',
				children: [{
					xtype: 'AckCheckboxField',
					optional: true,
					extraClass: 'small',
					name: i.id,
					shortText: i.title,
					value: !!i.selected,
				}],
			};
		}));
	},

	getCheckboxes() {
		return Array.from(this.itemsCt.querySelectorAll('li'))
			.map(li => li.children[0].gwComponent);
	},

	getFormFieldsData() {
		let selected = [];
		let order = [];

		for (let cb of this.getCheckboxes()) {
			if (cb.name) {
				if (cb.getValue()) {
					selected.push(cb.name);
				}
				order.push(cb.name);
			}
		}

		return {selected, order};
	},

	setItems(items) {
		this.items = items;
		this.renderItems(this.items);
		this.refreshSelectionButtons();
	},

});

// }}}

// Smaller widgets:

// {{{ Widgets.ToggleButton

GW.define('Widgets.ToggleButton', 'GW.Component', {
	state: 'pressed',

	initComponent() {
		this.el = document.gwCreateElement({
			nodeName: 'button',
			type: 'button',
			'on:click': ev => {
				ev.preventDefault();
				this.onClick();
			},
		});

		this.setState(this.state);
	},

	getStates() {
		return [{
			id: 'pressed',
			cls: 'primary',
			text: '1',
		}, {
			id: 'depressed',
			cls: 'secondary',
			text: '0',
		}];
	},

	onClick() {
		this.toggle();
		this.fire('change');
	},

	toggle() {
		let states = this.getStates();

		let idx = states.findIndex(s => s.id == this.state);

		idx = (idx + 1) % states.length;

		this.setState(states[idx].id);
	},

	renderState(state) {
		this.el.className = state.cls;

                let els = [state.text];

		if (state.icon) {
			if (this.el.classList.contains('icon-right')) {
				els.push(Utils.useIcon(state.icon));
			} else {
				els.unshift(Utils.useIcon(state.icon));
			}
		}

		this.el.gwReplaceChildren(els);
		this.el.classList.toggle('hidden', this.hidden);
	},

	setHidden(hidden) {
		this.hidden = !!hidden;
		this.el.classList.toggle('hidden', this.hidden);
	},

	setState(id) {
		let states = this.getStates();
		let state = states.find(s => s.id == id);

		this.state = state.id;
		this.renderState(state);
	},

	getState() {
		return this.state;
	},
});

// }}}
// {{{ Widgets.ExpanderToggleButton

GW.define('Widgets.ExpanderToggleButton', 'Widgets.ToggleButton', {
	state: 'collapsed',
	expandCount: 0,

	getStates() {
		return [{
			id: 'collapsed',
			cls: 'button-expander-button transparent icon-right small',
			icon: 'arrow-simple-down',
			text: _('Více') + (this.expandCount > 0 ? '(' + this.expandCount + ')' : ''),
		}, {
			id: 'expanded',
			cls: 'button-expander-button transparent icon-right small',
			icon: 'arrow-simple-up',
			text: _('Méně'),
		}];
	},

	setExpandCount(count) {
		this.expandCount = count;

		if (this.state == 'collapsed') {
			this.setState(this.state);
		}
	},
});

// }}}
// {{{ Widgets.ItemSwitcher

GW.define('Widgets.ItemSwitcher', 'GW.Component', {
	cls: undefined,
	itemCls: 'item',
	items: [],

	initComponent() {
		this.el = document.gwCreateElement({
			className: this.cls,
			'on*:click': ev => {
				let itemEl = ev.target;
				while (itemEl && itemEl.parentNode !== this.el) {
					itemEl = itemEl.parentNode;
				}

				if (!itemEl || itemEl.disabled) {
					return;
				}

				let i = itemEl.$data;
				if (i.href && i.href != '#') {
					return;
				}

				ev.preventDefault();

				if (i.handle) {
					i.handle(i);
				}

				this.fire('beforeselect', i);
			},
		}, this);

		this.setItems(this.items);
	},

	getItems() {
		return Array.from(this.el.children).filter(el => el.$data).map(el => el.$data);
	},

	renderItem(i) {
		return {
			$data: i,
			'attr:data-item-id': i.id,
			nodeName: i.href ? 'a' : 'button',
			disabled: !!i.disabled || !!this.disabled,
			className: [this.itemCls, i.cls].filter(v => v).join(' '),
			href: i.href || '#',
			children: [i.icon ? Utils.useIcon(i.icon) : undefined, {
				nodeName: 'span',
				className: 'title',
				textContent: i.title,
			}],
		};
	},

	setItems(items) {
		let sel = this.getSelected();

		this.el.gwReplaceChildren(items.map(i => this.renderItem(i)));

		this.setSelected(sel);
	},

	setSelected(id) {
		for (let el of this.el.children) {
			el.classList.toggle('active', el.$data.id === id);
		}
	},

	getSelected() {
		for (let el of this.el.children) {
			if (el.classList.contains('active')) {
				return el.$data.id;
			}
		}

		return null;
	},

	getSelectedItemData() {
		for (let el of this.el.children) {
			if (el.classList.contains('active')) {
				return el.$data;
			}
		}

		return null;
	},

	setDisabled(disabled) {
		this.disabled = !!disabled;

		for (let el of this.el.children) {
			el.disabled = !!el.$data.disabled || !!this.disabled;
		}
	},
});

// }}}
// {{{ Widgets.TabSwitcher

GW.define('Widgets.TabSwitcher', 'Widgets.ItemSwitcher', {
	cls: 'tab-switcher',
	itemCls: 'tab',

	'before:initComponent'() {
		this.notifications = {};
	},

	renderNotifications() {
		for (let el of this.el.children) {
			if (!el.$data) {
				continue;
			}

			let notify = this.notifications[el.$data.id];
			if (notify) {
				if (!el.$notifyEl) {
					el.$notifyEl = el.gwCreateChild({
						nodeName: 'span',
						className: 'notify',
					});
				}

				let newText = (typeof notify == 'string') ? notify : '';
				if (el.$notifyEl.textContent != newText) {
					el.$notifyEl.textContent = newText;
				}
			} else if (el.$notifyEl) {
				el.$notifyEl.remove();
				delete el.$notifyEl;
			}
		}
	},

	setNotification(tabId, notify) {
		if (typeof notify === 'number') {
			notify = notify > 0 && String(notify > 99 ? '99+' : notify);
		}
		
		this.notifications[tabId] = notify;
		this.renderNotifications();
	},

	'after:setItems'() {
		this.renderNotifications();
	},
});

// }}}
// {{{ Widgets.ButtonSwitcher

GW.define('Widgets.ButtonSwitcher', 'Widgets.ItemSwitcher', {
	cls: 'button-switcher small',
});

// }}}
// {{{ Widgets.CardSwitcher

/*
 * Component that keeps track of several subcomponents and allows switching
 * between them.
 */
GW.define('Widgets.CardSwitcher', 'GW.Component', {
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'card-switcher',
		}, this);

		this.lastActivation = Symbol('last-activation');
	},

	activateCard(c, config) {
		this.el.dataset.cardid = c.id || '';
		this.el.dataset.cardtype = c.constructor.__className__;

		c[this.lastActivation] = performance.now();

		if (this.el.firstElementChild !== c.el) {
			this.el.prepend(c.el);
		}

		if (c.applyParams) {
			return c.applyParams(config.params);
		}
	},

	/*
	 * This either activates the existing subcomponent or instantiates a new
	 * one. Only components with id are preserved in the
	 * background. Components with no id set are considered transient.
	 *
	 * Activation process:
	 * - configure new values for data-cardid, data-cardtype on the
	 *   container
	 * - call applyParams(config.params)
	 */
	async show(config) {
		for (let el of Array.from(this.el.children)) {
			let c = el.gwComponent;
			if (c && config.id && c.id === config.id) {
				await this.activateCard(c, config);
				return c;
			} else if (c && !c.id) {
				c.destroy();
			} else if (!c) {
				el.remove();
			}
		}

		let c = GW.create(config);

		await this.activateCard(c, config);

		return c;
	},

	/*
	 * Destroy all non-visible subcomponents.
	 */
	prune() {
		for (let el of Array.from(this.el.children).slice(1)) {
			if (el.gwComponent) {
				el.gwComponent.destroy();
			} else {
				el.remove();
			}
		}
	},
	
	remove(id) {
		for (let el of Array.from(this.el.children)) {
			if (el.gwComponent && el.gwComponent.id === id) {
				el.gwComponent.destroy();
			}
		}
	},

	getCards() {
		return Array.from(this.el.children).map(el => el.gwComponent || el);
	},
});

// }}}
// {{{ Widgets.DateRangeInput

GW.define('Widgets.DateRangeInput', 'GW.Component', {
	
	rangesOptions: [{
			value: 'today',
			text: _('Dnes'),
			getRange() {
				let now = new Date;
				let since = new Date(now.getFullYear(), now.getMonth(), now.getDate());
				return [since];
			},
		}, {
			value: 'yesterday',
			text: _('Včera'),
			getRange() {
				let now = new Date;
				let until = new Date(now.getFullYear(), now.getMonth(), now.getDate());
				until.setDate(until.getDate() - 1);
				let since = new Date(until);
				return [since, until];
			},
		}, {
			value: 'thisweek',
			text: _('Tento týden'),
			getRange() {
				let now = new Date;
				let since = new Date(now.getFullYear(), now.getMonth(), now.getDate());
				since.setDate(since.getDate() - (since.getDay() || 7) + 1);
				return [since];
			},
		}, {
			value: 'lastweek',
			text: _('Minulý týden'),
			getRange() {
				let now = new Date;
				let until = new Date(now.getFullYear(), now.getMonth(), now.getDate());
				until.setDate(until.getDate() - (until.getDay() || 7));
				let since = new Date(until);
				since.setDate(since.getDate() - 6);
				return [since, until];
			},
		}, {
			value: 'last7days',
			text: _('Posledních 7 dnů'),
			getRange() {
				let now = new Date;
				let since = new Date(now.getFullYear(), now.getMonth(), now.getDate());
				since.setDate(since.getDate() - 6); // 7 days including today
				return [since];
			},
		}, {
			value: 'thismonth',
			text: _('Tento měsíc'),
			getRange() {
				let now = new Date;
				let since = new Date(now.getFullYear(), now.getMonth(), 1);
				return [since];
			},
		}, {
			value: 'lastmonth',
			text: _('Minulý měsíc'),
			getRange() {
				let now = new Date;
				let until = new Date(now.getFullYear(), now.getMonth(), 1);
				until.setDate(until.getDate() - 1);
				let since = new Date(until);
				since.setDate(1);
				return [since, until];
			},
		}, {
			value: 'last30days',
			text: _('Posledních 30 dnů'),
			getRange() {
				let now = new Date;
				let since = new Date(now.getFullYear(), now.getMonth(), now.getDate());
				since.setDate(since.getDate() - 29); // 30 days including today
				return [since];
			},
		}, {
			value: 'thisyear',
			text: _('Tento rok'),
			getRange() {
				let now = new Date;
				let since = new Date(now.getFullYear(), 0, 1);
				return [since];
			},
		}, {
			value: 'lastyear',
			text: _('Minulý rok'),
			getRange() {
				let now = new Date;
				let until = new Date(now.getFullYear(), 0, 1)
				until.setDate(until.getDate() - 1);
				let since = new Date(until);
				since.setMonth(0);
				since.setDate(1);
				return [since, until];
			},
		}
	],
	
	initComponent() {
		this.el = document.gwCreateElement({
			className: 'date-range-input',
			children: [{
				ref: 'rangeField',
				xtype: 'SelectField',
				extraClass: 'small',
				label: _('Období'),
				autofocus: true,
				popupAutoWidth: false,
				options: [
					...this.rangesOptions,
					{
						value: 'custom',
						text: _('Vlastní'),
						// default value for custom range is 'today' range:
						getRange: () => this.rangesOptions.filter(r => r.value === 'today').map(r => r.getRange())[0] || [],
					},
				],
				'on:change': c => {
					this.setTimesForSelectedOption();
					this.fire('change');
				},
			}, {
				ref: 'sinceField',
				xtype: 'TextField',
				extraClass: 'small',
				type: 'date',
				label: _('Od'),
				optional: true,
				'on:change': c => {
					this.rangeField.setValue('custom');
					this.updateTimeFields();
					this.fire('change');
				},
			}, {
				ref: 'sinceTimeField',
				xtype: 'TextField',
				extraClass: 'small time',
				type: 'time',
				label: '',
				optional: true,
				'on:change': c => {
					this.rangeField.setValue('custom');
					this.fire('change');
				},
			}, {
				ref: 'untilField',
				xtype: 'TextField',
				extraClass: 'small',
				type: 'date',
				label: _('Do'),
				optional: true,
				'on:change': c => {
					this.rangeField.setValue('custom');
					this.updateTimeFields();
					this.fire('change');
				},
			}, {
				ref: 'untilTimeField',
				xtype: 'TextField',
				extraClass: 'small time',
				type: 'time',
				label: '',
				optional: true,
				'on:change': c => {
					this.rangeField.setValue('custom');
					this.fire('change');
				},
			}]
		}, this);
	},

	async setRangeOption(range) {
		await this.rangeField.setValue(range || 'custom');
		this.setTimesForSelectedOption();
	},
	
	/*
	 * Call this when date field is updated, to update the state of related
	 * time fields.
	 */
	updateTimeFields() {
		this.sinceTimeField.setDisabled(!this.sinceField.getValue());
		if (!this.sinceField.getValue()) {
			this.sinceTimeField.setValue(null);
		}

		this.untilTimeField.setDisabled(!this.untilField.getValue());
		if (!this.untilField.getValue()) {
			this.untilTimeField.setValue(null);
		}
	},

	/*
	 * Call this to fill date/time fields based on the symbolic range select.
	 */
	setTimesForSelectedOption() {
		let option = this.rangeField.getSelectedOption();
		let getRange = option && option.getRange;
		if (getRange) {
			let [since, until] = getRange();

			this.setTimeRange(since, until);
		}
	},

	getTimeRange() {
		let since_date = this.sinceField.getValue();
		let until_date = this.untilField.getValue();
		let since_time = this.sinceTimeField.getValue();
		let until_time = this.untilTimeField.getValue();

		function format_datetime(date, time) {
			if (!date) {
				return null;
			}
			if (time && time.match(/^\d+:\d+$/)) {
				// add seconds to conform ISO
				time += ':00';
			}
			return date + (time ? ('T' + time) : '');
		}

		return [
			format_datetime(since_date, since_time),
			format_datetime(until_date, until_time), 
		];
	},
	
	getRange() {
		return this.rangeField.getValue();
	},

	setTimeRange(since, until) {
		since = since ? Utils.parseDateIso(since) : null;
		until = until ? Utils.parseDateIso(until) : null;
		
		this.sinceField.setValue(since ? Utils.formatDateIso(since) : null);
		this.untilField.setValue(until ? Utils.formatDateIso(until) : null);

		const p = n => String(n).padStart(2, '0');

		function formatTime(time) {
			if (!time) {
				return null;
			}
			let s = p(time.getHours()) + ':' + p(time.getMinutes());
			if (time.getSeconds()) {
				s += ':' + p(time.getSeconds());
			}

			return s === '00:00' ? null : s;
		}

		this.sinceTimeField.setValue(formatTime(since));
		this.untilTimeField.setValue(formatTime(until));

		this.updateTimeFields();
	},
	
});

// }}}
// {{{ Widgets.TabbedCardPanel

GW.define('Widgets.TabbedCardPanel', 'GW.Component', {
	queryParam: 'tab',

	initComponent() {
		this.el = document.gwCreateElement({
			className: 'tabbed-card-panel ' + this.cls,
			children: [{
				ref: 'toolbar',
				className: 'toolbar',
				children: [{
					ref: 'tabs',
					xtype: 'Widgets.TabSwitcher',
					'on:beforeselect': (c, i) => {
						Application.route({
							paramsFn: params => {
								params.set(this.queryParam, i.id);
								return params;
							},
						});
					},
				}],
			}, {
				ref: 'cards',
				xtype: 'Widgets.CardSwitcher',
			}],
		}, this);

		this.tabs.setItems(this.getTabs());
	},

	getTabs() {
		return [];
	},

	showEmpty(card) {
		this.toolbar.gwHide(true);
		this.cards.show({
			...card,
			id: '__empty',
		});
	},

	async applyParams(params) {
		let tabs = this.tabs.getItems();
		let tabId = params.get(this.queryParam);
		let tab = tabs.find(t => t.id == tabId);
		if (!tab) {
			tab = tabs[0];
		}

		this.tabs.setSelected(tab.id);
		this.toolbar.gwHide(false);

		let card = await this.cards.show({
			id: tab.id,
			...tab.card,
			params,
		})

		return {
			tab,
			tabId,
			tabs,
			card,
		};
	},
});

// }}}
// {{{ Widgets.EditableItemSelector

GW.define('Widgets.EditableItemSelector', 'GW.Component', {
	emptyListText: _('Žádné skupiny nejsou dostupné'),
	addButtonText: _('Přidat skupinu'),
	createTitle: _('Nová skupina'),
	editTitle: _('Upravit skupinu'),
	deleteTitle: _('Opravdu chcete smazat skupinu "{name}"?'),
	itemApiPath: 'user-groups',
	activeUserGroup: undefined,
	itemQueryParam: 'group',
	allowCreate: true,
	allowDelete: true,
	allowEdit: true,

	initComponent() {
		this.el = document.gwCreateElement({
			className: 'editable-item-selector',
			children: [{
				ref: 'listEl',
				className: 'vertical-menu',
			}, {
				ref: 'footerEl',
				className: 'footer',
				children: [{
					':skip': !this.allowCreate,
					nodeName: 'button',
					type: 'button',
					className: 'secondary small icon-left button-block',
					children: [Utils.useIcon('plus'), this.addButtonText],
					'on:click': ev => this.createItem(),
				}],
			}],
		}, this);
	},

	renderList(groups) {
		this.listEl.gwReplaceChildren(groups.map(item => {
			return {
				$data: item,
				className: 'item',
				children: [{
					nodeName: 'a',
					href: this.getItemLink(item),
					className: 'name',
					textContent: this.getItemLabel(item),
				}, {
					':skip': this.getItemMenuOptions(item).filter(i => !i.hidden).length == 0,
					nodeName: 'button',
					type: 'button',
					className: 'transparent small icon-only menu',
					children: [Utils.useIcon('vdots')],
					'on:click': ev => {
						ev.preventDefault();

						this.showItemMenu(ev, item);
					},
				}]
			};
		}));

		if (groups.length == 0) {
			this.listEl.gwCreateChild({
				className: 'empty',
				textContent: this.emptyListText,
			});
		}
	},

	showItemMenu(ev, item) {
		let popup = new PopupMenu({
			options: this.getItemMenuOptions(item),
		});

		PopupManager.show(popup, {
			anchorEl: ev.target.closest('.menu'),
			forceAxis: 'vertical',
			preferReadingOrder: true,
		});
	},

	/*
	 * Overridable methods:
	 */

	getItemLink(item) {
		return '?' + this.itemQueryParam + '=' + encodeURIComponent(item.id);
	},

	getItemLabel(item) {
		return item.name;
	},

	getItemMenuOptions(item) {
		return [{
			hidden: !this.allowEdit,
			iconName: 'pencil',
			text: _('Upravit'),
			handle: this.editItem.bind(this, item),
		}, {
			hidden: !this.allowDelete,
			iconName: 'cross',
			text: _('Smazat'),
			handle: this.deleteItem.bind(this, item),
		}];
	},

	getItemFormFields() {
		return [{
			xtype: 'TextField',
			name: 'name',
			label: _('Jméno'),
		}];
	},

	createItem() {
		let me = this;

		new Main.FormDialog({
			title: this.createTitle,

			renderFormFields() {
				return me.getItemFormFields();
			},

			async onSave(data) {
				return await REST.POST(me.itemApiPath, data);
			},

			'on:aftersave': (c, data) => {
				Utils.clearCache(me);

				me.fire('itemcreate', data);
			},
		});
	},

	editItem(item) {
		let me = this;

		let dlg = new Main.FormDialog({
			title: this.editTitle,

			renderFormFields() {
				return me.getItemFormFields();
			},

			async onSave(data) {
				return await REST.PUT(me.itemApiPath + '/' + item.id, data);
			},

			'on:aftersave': (c, data) => {
				Utils.clearCache(me);

				me.fire('itemupdate', item);
			},
		});

		dlg.setFormFieldsData(item);
	},

	deleteItem(item) {
		let me = this;

		new Main.ConfirmDialog({
			title: this.deleteTitle.replace('{name}', item.name),
			subtitle: _('Tato akce nelze vzít zpět.'),

			async onSave() {
				return await REST.DELETE(me.itemApiPath + '/' + item.id);
			},

			'on:aftersave': (c, data) => {
				Utils.clearCache(me);

				me.fire('itemdelete', item);
			},
		});
	},

	async applyParams(params) {
		let signal = Utils.abortAndRetry(this, 'load-data');
		let [items, itemsChanged] = await Utils.cache(this, 'items', 60000,
			REST.GET, this.itemApiPath, null, {signal, activeUserGroup: this.activeUserGroup});
		if (itemsChanged) {
			this.renderList(items);
		}

		// get item that should be active
		let item = items.find(g => g.id == Number(params.get(this.itemQueryParam)));
		if (!item) {
			item = items[0];
		}

		Utils.selectListItem(this.listEl, '.item', 'active', i => i.$data.id === item.id);

		return {
			items,
			item,
		};
	},
});

// }}}
// {{{ Widgets.EntityListEditor

GW.define('Widgets.EntityListEditor', 'GW.Component', {
	entityApiPrefix: null,
	createText: _('Nová'),
	selectText: _('Vyberte ze seznamu'),
	editText: _('Upravit'),
	deleteText: _('Opravdu smazat?'),
	idProp: 'id',
	defaultFilterOption: null,
	routedState: true,

	getFilterOptions() {
		return [];
	},

	filterListItem(i, match, filter) {
		return true;
	},

	async entityGetAll(params, signal) {
		return await REST.GET(this.entityApiPrefix, params, {signal});
	},

	async entityCreate(data) {
		return await REST.POST(this.entityApiPrefix, data);
	},

	async entityUpdate(e, data) {
		return await REST.PUT(this.entityApiPrefix + '/' + e[this.idProp], data);
	},

	async entityDelete(e) {
		return await REST.DELETE(this.entityApiPrefix + '/' + e[this.idProp]);
	},

	getEntityTableColumns() {
		return this.getEntityProperties().filter(c => !c.detailOnly);
	},

	getEntityDetailProperties() {
		return this.getEntityProperties();
	},

	getCreateFormFields() {
		return [];
	},

	getEditFormFields(item) {
		return this.getCreateFormFields();
	},

	getEntityProperties() {
		return [];
	},

	allowAction(item, action) {
		return true;
	},

	initComponent() {
		this.el = document.gwCreateElement({
			className: 'entity-list-editor' + (this.renderDetail ? '' : ' no-detail'),
			children: [{
				className: 'header',
				children: [{
					':skip': !this.title,
					className: 'title',
					textContent: this.title,
				}, {
					ref: 'toolbarCt',
					className: 'toolbar',
					children: this.getToolbarItems(),
				}],
			}, {
				ref: 'table',
				xtype: 'FilterableSmartTable',
				getColumns: () => {
					return this.getEntityTableColumns().concat(this.getControlColumns());
				},
				selectable: !!this.renderDetail,
				onRowClick: (row, tr, ev) => {
					this.setSelectedItem(row[this.idProp]);
				},
			}, {
				':skip': !this.renderDetail,
				className: 'detail',
				ref: 'detailCt',
			}],
		}, this);
	},

	setDisabled(disabled) {
		this.disabled = !!disabled;
		this.toolbarCt.querySelectorAll('input,button')
			.forEach(el => el.disabled = this.disabled);
	},

	getControlColumns() {
		let me = this;

		if (this.renderDetail) {
			return [];
		}

		return [{
			id: 'ctl',
			name: '',
			formatCell(td, v, row) {
				me.renderControlCell(td, row);
			},
		}];
	},

	renderControlCell(td, item) {
		let options = this.getDetailActions(item).filter(o => !o.hidden);
		if (options.length == 0) {
			return;
		}

		let menuOptions = options.filter(o => o.secondary);
		let mainOptions = options.filter(o => !o.secondary);

		for (let o of mainOptions) {
			td.gwCreateChild({
				nodeName: 'button',
				className: 'secondary small icon-only',
				type: 'button',
				title: o.text,
				children: [Utils.useIcon(o.iconName || 'pencil')],
				'on:click': ev => o.handle(),
			});
		}

		if (menuOptions.length > 0) {
			td.gwCreateChild({
				nodeName: 'button',
				className: 'secondary small icon-only',
				type: 'button',
				children: [Utils.useIcon('vdots')],
				'on:click': ev => this.showItemMenu(ev, menuOptions),
			});
		}
	},

	showItemMenu(ev, options) {
		let popup = new PopupMenu({
			options,
		});

		PopupManager.show(popup, {
			anchorEl: ev.target.closest('button'),
			forceAxis: 'vertical',
			preferReadingOrder: true,
		});
	},

	getToolbarItems() {
		let filterOpts = this.getFilterOptions();

		return [{
			ref: 'btnCreate',
			nodeName: 'button',
			type: 'button',
			className: 'primary small icon-left',
			children: [Utils.useIcon('plus'), this.createText],
			'on:click': ev => this.onCreate(),
		}, {
			ref: 'searchEl',
			nodeName: 'input',
			type: 'search',
			className: 'search small',
			placeholder: _('Hledat'),
			'on:input': ev => {
				this.setSearchQuery(ev.target.value);
			},
		}, {
			':skip': filterOpts.length == 0,
			ref: 'filterSelect',
			xtype: 'SelectField',
			cls: 'small',
			label: _('Stav'),
			options: filterOpts,
			'on:change': c => {
				this.setFilterValue(c.getValue());
			},
		}, {
			className: 'fill',
		}, ...this.getExtraToolbarItems()];
	},

	setSelectedItem(id) {
		if (!this.routedState) {
			this.selectedItemId = id;
			this.reload();
			return;
		}

		Application.route({
			paramsFn: params => {
				if (id != null) {
					params.set('id', id);
				} else {
					params.delete('id');
				}

				return params;
			},
		});
	},

	getSelectedItem() {
		if (!this.routedState) {
			return this.selectedItemId;
		}

		let params = new URLSearchParams(location.search);

		return params.get('id');
	},

	setFilterValue(query) {
		if (!this.routedState) {
			this.selectedFilter = query;
			this.reload();
			return;
		}

		Application.route({
			paramsFn: params => {
				params.set('filter', query);
				return params;
			},
		});
	},

	getFilterValue() {
		if (!this.routedState) {
			return this.selectedFilter;
		}

		let params = new URLSearchParams(location.search);

		return params.get('filter');
	},

	setSearchQuery(query) {
		if (!this.routedState) {
			this.searchQuery = query;
			this.reload();
			return;
		}

		Application.route({
			paramsFn: params => {
				params.set('q', query);
				return params;
			},
		});
	},

	getSearchQuery() {
		if (!this.routedState) {
			return this.searchQuery;
		}

		let params = new URLSearchParams(location.search);

		return params.get('q');
	},

	getExtraToolbarItems() {
		return [];
	},

	clearDetail() {
		this.detailCt.gwReplaceChildren([{
			className: 'fullscreen-message',
			textContent: this.selectText,
		}]);
	},

	getDetailActions(item) {
		return [{
			hidden: !this.allowAction(item, 'edit'),
			iconName: 'pencil',
			text: _('Upravit'),
			handle: () => this.onEdit(item),
		}, ...this.getExtraDetailActions(item), {
			hidden: !this.allowAction(item, 'delete'),
			iconName: 'delete',
			text: _('Smazat'),
			handle: () => this.onDelete(item),
		}];
	},

	getExtraDetailActions(item) {
		return [];
	},

	renderDetail(item) {
		let buttons = this.getDetailActions(item).filter(a => !a.hidden).map(a => {
			return {
				nodeName: 'button',
				className: 'primary small' + (a.iconName ? ' icon-left' : ''),
				children: a.iconName ? [Utils.useIcon(a.iconName), a.text] : [a.text],
				'on:click': ev => a.handle(),
			};
		});

		this.detailCt.gwReplaceChildren([{
			className: 'side-buttons',
			children: buttons,
		}, {
			nodeName: 'h1',
			className: 'h3',
			textContent: item.name,
		}, {
			xtype: 'PropertyTable',
			value: item,
			properties: this.getEntityDetailProperties(),
		}]);
	},

	async preloadData() {
	},

	getFilterQuery() {
		let filterOpts = this.getFilterOptions();
		let filter = filterOpts.length > 0 ? this.getFilterValue() || this.defaultFilterOption || filterOpts[0].value : null;
		let q = this.getSearchQuery() || '';

		if (this.filterSelect) {
			this.filterSelect.setValue(filter);
		}

		if (this.searchEl.value != q) {
			this.searchEl.value = q;
		}

		let query = {
			q,
			filter,
		};

		this.updateFilterQuery(query);

		return query;
	},

	updateFilterQuery(query) {
	},

	createQueryMatcher(query) {
		return TextUtils.createMatchAll(query);
	},

	async loadList(query) {
		let signal = Utils.abortAndRetry(this, 'reload');
		let queryKey = JSON.stringify(query);

		let [list, listChanged] = await Utils.cache(this, 'list', 60000, this.entityGetAll.bind(this), null, signal);

		if (listChanged || queryKey != this.lastQueryKey) {
			this.lastQueryKey = queryKey;

			let match = this.createQueryMatcher(query.q);

			this.table.setData(list.filter(i => this.filterListItem(i, match, query)));
		}

		return list;
	},

	async loadDetail(list) {
		let id = this.getSelectedItem();

		this.table.selectRow(row => row[this.idProp] == id);

		let item = list.find(item => item[this.idProp] == id);
		if (item) {
			this.renderDetail(item);
		} else {
			this.clearDetail();
		}
	},

	async applyParams() {
		this.btnCreate.classList.toggle('hidden', !this.allowAction(null, 'create'));

		await this.preloadData();

		let list = await this.loadList(this.getFilterQuery());
		if (this.renderDetail) {
			await this.loadDetail(list);
		}
	},

	clearCache() {
		Utils.clearCache(this);
	},

	async reload() {
		Utils.clearCache(this);

		await this.applyParams();
	},

	onCreate() {
		let me = this;
		let dlg = new Main.FormDialog({
			title: this.createText,

			renderFormFields() {
				return me.getCreateFormFields();
			},

			async onSave(data) {
				let item = await me.entityCreate(data);

				Utils.clearCache(me);

				me.setSelectedItem(item[me.idProp]);
			},
		});

		return dlg;
	},

	onEdit(item) {
		let me = this;
		let dlg = new Main.FormDialog({
			title: this.editText,

			renderFormFields() {
				return me.getEditFormFields(item);
			},

			async onSave(data) {
				await me.entityUpdate(item, data);
				await me.reload();
			},
		});

		dlg.setFormFieldsData(this.getEditDialogData(item));

		return dlg;
	},

	getEditDialogData(item) {
		return item;
	},

	onDelete(item) {
		let me = this;

		new Main.ConfirmDialog({
			title: typeof this.deleteText == 'function' ? this.deleteText(item) : this.deleteText,
			subtitle: _('Tato akce nelze vzít zpět.'),

			async onSave() {
				return await me.entityDelete(item);
			},

			'on:aftersave': (c, data) => {
				Utils.clearCache(me);

				me.setSelectedItem();
			},
		});
	},
});

// }}}

// Mixins and extensions

Features = {};

// {{{ Features.horizontalSplitResizer

Features.horizontalSplitResizerMixin = function({storageKey, leftEl, lifetimeComponent}) {
	if (!leftEl || !leftEl.nextElementSibling) {
		return;
	}

	let draggerEl = leftEl.gwCreateChild({
		className: 'dragger',
	});

	let ct = leftEl.parentNode;
	let dragAllowed;
	let PRIMARY_MASK = 1;

	ct.addEventListener('mousedown', ev => {
		if (ev.buttons & PRIMARY_MASK) {
			applyDraggingState(dragAllowed = ev.target === draggerEl);
		}
	}, true);

	function onMouseup(ev) {
		if (!(ev.buttons & PRIMARY_MASK)) {
			applyDraggingState(dragAllowed = false);
		}
	}

	document.addEventListener('mouseup', onMouseup, true);

	if (lifetimeComponent) {
		lifetimeComponent.on('destroy', c => {
			document.removeEventListener('mouseup', onMouseup, true);
		});
	}

	ct.addEventListener('mousemove', ev => {
		if ((ev.buttons & PRIMARY_MASK) && dragAllowed) {
			let leftBox = leftEl.getBoundingClientRect();
			let widthRatio = (ev.pageX - leftBox.left) / ct.clientWidth;

			applyRatio(widthRatio);
			localStorage.setItem(storageKey, widthRatio);
		}
	}, true);

	applyRatio(parseFloat(localStorage.getItem(storageKey)));

	function applyRatio(ratio) {
		if (isFinite(ratio)) {
			ratio = Math.max(0, Math.min(1, ratio));

			leftEl.style.flexBasis = (ratio * 100) + '%';
			leftEl.nextElementSibling.style.flexBasis = (100 - (ratio * 100)) + '%';
		}
	}

	function applyDraggingState(dragging) {
		ct.classList.toggle('no-selection', !!dragging);
	}
};

// }}}
// {{{ Features.expandingContainer

/*
 * Makes a container expandable using a given button if the items in the
 * container would overflow a single line. Button is hidden if no overflow
 * is present.
 */
Features.expandingListContainer = function({container, expanderButton, onExpanded}) {
	expanderButton.on('change', ev => {
		refresh();
	});

	function isExpanded() {
		return expanderButton.getState() == 'expanded';
	}

	let io = new IntersectionObserver(entries => {
		for (let e of entries) {
			e.target.$intersect = e;
		}

		updateButton();
	}, {
		root: container,
	});

	function updateButton() {
		if (isExpanded()) {
			expanderButton.setExpandCount(0);
			expanderButton.setHidden(false);
                        return;
		}

		let countOverscroll = 0;

		for (let el of container.children) {
			let e = el.$intersect;
			if (e && !e.isIntersecting) {
				countOverscroll++;
			}
		}

		expanderButton.setExpandCount(countOverscroll);
		expanderButton.setHidden(countOverscroll == 0);
	}

	expanderButton.on('change', c => {
		container.classList.toggle('expanded', isExpanded());
		onExpanded && onExpanded(isExpanded());
	});

	function refresh() {
		for (let el of container.children) {
			io.observe(el);
		}
	}

	refresh();

	function destroy() {
		io.disconnect();
	}

	return {
		set expanded(val) {
			expanderButton.setState(val ? 'expanded' : 'collapsed');
			expanderButton.fire('change');
		},
		refresh,
		destroy,
	};
};

// }}}

// Drag'n'drop:

// {{{ DnDListSetup

function DnDListSetup(el, config) {
	var container = null, // container of draggable elements
		dragged = null,   // element being dragged
		ending = false,   // flag indicating that drag ending is currently proceeding
		height = 0,       // height of the dragged element
		margin = 0,       // margin between elements
		startY = 0,       // initial top position of the dragged element
		elMouseY = 0,     // Y offset of mouse pointer within the dragged element
		minY = 0,         // minimum top position where the element can be dragged
		maxY = 0,         // maximum top position where the element can be dragged
		els = null,       // list of elements
		elCenters = null, // list of positions of element centers
		startPos = 0,     // starting index of dragged element 
		pos = 0;          // current index of dragged element

	function getOffsetTop(el) {
		let off = 0;

		while (el) {
			off += el.offsetTop;
			el = el.offsetParent;
		}

		return off;
	}

	function dragStart(element, mouseY) {
		dragged = element;
		els = [];
		elCenters = [];
		height = dragged.offsetHeight;
		minY = getOffsetTop(container);
		maxY = minY + container.scrollHeight - dragged.offsetHeight;
		startY = minY + dragged.offsetTop;
		elMouseY = mouseY - startY;

		for (let child of container.querySelectorAll('.' + config.itemClass)) {
			els.push(child);
			elCenters.push(minY + child.offsetTop + child.offsetHeight / 2);

			if (child === dragged) {
				startPos = pos = els.length - 1; 
			}
		}

		margin = els.length > 1 ? (els[1].offsetTop - els[0].offsetTop - els[0].offsetHeight) : 0;

		container.classList.add('dragging');
		dragged.classList.add('dragging');

		config.onDragStart && config.onDragStart(dragged);
	}

	function getTranslation(el) {
		var t = /translateY\((.*)px\)/i.exec(el.style.transform);
		return t ? parseFloat(t[1]) : 0;
	}

	function dragMove(off) {
		var heightDiff = height - els[pos + off].offsetHeight; 

		els[pos + off].style.transform = 'translateY(' + (getTranslation(els[pos + off]) - off * (height + margin)) + 'px)';

		els[pos] = els[pos + off];
		els[pos + off] = dragged;

		if (heightDiff != 0) {
			var adj = (off > 0 ? -.5 : .5) * heightDiff;
			elCenters[pos + off] += adj;
			elCenters[pos] += adj;
		}

		pos += off;
	}

	function dragComplete() {
		ending = true;

		if (startPos !== pos) {
			var beforeEl = pos + 1 < els.length ? els[pos + 1] : null;
			dragged.parentNode.insertBefore(dragged, beforeEl);

			config.onMoveItem && config.onMoveItem(dragged, beforeEl);
			config.onDragEnd && config.onDragEnd(dragged);

			dragged.style.transform = 'translateY(' + (getTranslation(dragged) + startY - (minY + dragged.offsetTop)) + 'px)';
			dragged.offsetHeight;
		}

		container.classList.remove('dragging');
		container.offsetHeight;

		els.forEach(e => e.style.transform = '');

		setTimeout(() => {
			dragged.classList.remove('dragging');
			dragged = null;
			ending = false;
		}, 200);
	}

	container = el.querySelector('.drag-container') || el;
	container.classList.add('drag-container');

	var scrollCt = config.scrollCt || container;

	return new DnDSupport({
		containerEl: container,
		autoScroll: !!scrollCt,
		autoScrollEl: scrollCt,
		autoScrollLimits: {
			left: 20,
			right: 20,
			top: 20,
			bottom: 20
		},
		autoScrollDistance: 100,
		startFilter: ev => {
			if (config.onBeforeDrag && !config.onBeforeDrag(ev)) {
				return false;
			}

			return !!ev.target.closest('.' + config.startFilterClass);
		},
		'on:dragstart': (dnd, ev) => {
			// do not allow new dragging if previous dragging is still pending/animating
			if (dragged) {
				return;
			}

			// find dragged node
			dragged = dnd.lastDownTarget.closest('.' + config.itemClass);
			if (!dragged) {
				return;
			}

			dragStart(dragged, ev.pageY + scrollCt.scrollTop);
		},

		'on:dragend': () => {
			if (dragged && !ending) {
				dragComplete();
			}
		},

		'on:drag': (dnd, ev) => {
			if (!dragged || ending) {
				return;
			}

			if (ev.pageY <= 0) {
				// out of browser window
				return;
			}

			var y = Math.min(Math.max(scrollCt.scrollTop + ev.pageY - elMouseY, minY), maxY);

			dragged.style.transform = 'scale(1.0333) translateY(' + (y - startY) + 'px)';

			if (pos > 0 && elCenters[pos - 1] - 2 > y) {
				dragMove(-1);
			} else if (pos + 1 < elCenters.length && elCenters[pos + 1] + 2 < y + height) {
				dragMove(+1);
			}
		}
	});
}

// }}}
// {{{ DnDSupport

/**
 * Helper for implementing drag and drop functionality without native HTML5 APIs.
 * 
 * Monitors mouse activity within configured DnD container and fires 'dragstart', 'drag' and 'dragend' events.
 * 
 * Provides utility support for autoscrolling and drag images.
 * 
 * @todo Limit rate of drag events?
 * @todo What about touch events?
 */
GW.define('DnDSupport', 'GW.Object', {
	/**
	 * @cfg {DOMElement/string) Element where dragging should be monitored
	 */
	containerEl: undefined,

	/**
	 * @cfg {number} Number of pixels that need to be traveled with pressed mouse before dragging is considered started 
	 */
	initialDistance: 6,

	/**
	 * @cfg {string/function} Optional CSS selector or callback function that checks if dragging can be started on a certain element.
	 * 
	 * Dragging will be allowed if the element matches the selector or the callback function returns true when called with 'mousedown' event as first argument.
	 */
	startFilter: null,

	/**
	 * @cfg {boolean} Enables/disables automatic suppression of dragging inside editable elements (such as input, textarea or contenteditable) 
	 */
	suppressInEditableElements: true,

	/**
	 * @cfg {boolean} Enables/disables automatic blurring of editable elements while dragging 
	 */
	blurEditableElements: true,

	/**
	 * @cfg {boolean} Enables/disables autoscroll
	 */
	autoScroll: true,

	/**
	 * @cfg {object} Autoscroll limits measured in pixels from container element edge
	 * 
	 * When autoscroll is enabled and mouse moves into one of the autoscroll zones, container gets automatically scrolled.
	 * Drag events are not fired during scrolling
	 */
	autoScrollLimits: {
		left: 40,
		right: 40,
		top: 40,
		bottom: 40
	},

	/**
	 * @cfg {number} Number of pixels to autoscroll at once
	 */
	autoScrollDistance: 125,

	/**
	 * @cfg {number} Duration of each individual autoscroll animation in milliseconds
	 */
	autoScrollDuration: 175,

	/**
	 * @cfg {DOMElement} Element which should be autoscrolled (optional, defaults to the container itself)
	 */
	autoScrollEl: undefined,

	/**
	 * @property {boolean} True/false indicating that dragging is in progress
	 */
	dragging: false,

	/**
	 * @property {DOMElement} Target of the last 'mousedown' event
	 */
	lastDownTarget: null,

	/**
	 * @property {object} Mouse offset within target element in last 'mousedown' event
	 */
	lastDownTargetOffset: null,

	/**
	 * @property {MouseEvent} Last 'mousedown' event
	 */
	lastDownEvent: null,

	/**
	 * @property {MouseEvent} Last 'mousemove' event
	 */
	lastMoveEvent: null,

	initObject() {
		let me = this, pressed = false;
		let isTouch = Application.isMobileUI();
		let trackedTouch, touchStartTime, touchStartTimer, touchStartCoords, touchEmuBlock;

		function distance(a, b) {
			let dx = Math.pow(a.pageX - b.pageX, 2);
			let dy = Math.pow(a.pageY - b.pageY, 2);

			return Math.sqrt(dx + dy);
		}

		// we only handle the first finger and ignore the rest
		// - we wait for 250ms if the user doesn't move the finger, we initiate DnD
		// - if the user moves or releases the finger within 250ms, we don't do anthing
		//   and leave the browser to do the native thing (click/scroll)
		function onTouch(ev) {
			let type, touch, generateClick;

			switch (ev.type) {
				case "touchstart":
					if (!trackedTouch) {
						// start tracking the first touch, but don't emulate mouse events
						// just yet
						touch = ev.changedTouches[0];
						trackedTouch = touch.identifier;
						touchStartCoords = {pageX: touch.pageX, pageY: touch.pageY};

						// if this touch is outside of
						// draggable element, skip the
						// emulation, to avoid scroll
						// cancelation on long press
						if (!me.checkStartFilter(ev)) {
							touchEmuBlock = true;
							return;
						}

						touchStartTimer = setTimeout(() => {
							// after 250ms send mousedown
							touchStartTimer = null;
							type = "mousedown";
							emulateEvents();
						}, 250);

						// don't prevent the action
						// right now
						return;
					}
					break;

				case "touchend":
				case "touchcancel":
				case "touchmove":
					touch = Array.from(ev.changedTouches).find(t => t.identifier == trackedTouch);
					if (touch) {
						if (ev.type == 'touchmove') {
							type = 'mousemove';

							// if touch moved too much, cancel the
							// timeout and block rest of this touch from
							// mouse events emulation(user is scrolling
							// or something)
							if (distance(touch, touchStartCoords) > 50 && touchStartTimer) {
								touchEmuBlock = true;
								clearTimeout(touchStartTimer);
								touchStartTimer = null;
							}
						} else {
							// stop tracking the touch
							type = 'mouseup';
							trackedTouch = null;
							touchStartCoords = null;
						}

						if (touchStartTimer || touchEmuBlock) {
							// if we're still waiting 500ms do nothing
							// or cancel the wait on touchcancel/end
							if (ev.type != 'touchmove') {
								clearTimeout(touchStartTimer);
								touchStartTimer = null;
								touchEmuBlock = false;
							}
							return;
						}
					}
					break;
			}

			try {
				ev.preventDefault();
			} catch (ex) {
				console.log(ex);
			}

			function emulateEvents() {
				if (touch && type) {
					let mev = new MouseEvent(type, {
						screenX: touch.screenX,
						screenY: touch.screenY,
						clientX: touch.clientX,
						clientY: touch.clientY,
						ctrlKey: ev.ctrlKey,
						altKey: ev.altKey,
						shiftKey: ev.shiftKey,
						metaKey: ev.metaKey,
						button: 0,
						buttons: 1,
						view: window,
						composed: true,
						bubbles: true,
						cancelable: true,
					});

					mev.$dnd_trusted = true;

					ev.target.dispatchEvent(mev);
				}

				if (generateClick) {
					let mev = new MouseEvent('click', {
						screenX: touch.screenX,
						screenY: touch.screenY,
						clientX: touch.clientX,
						clientY: touch.clientY,
						ctrlKey: ev.ctrlKey,
						altKey: ev.altKey,
						shiftKey: ev.shiftKey,
						metaKey: ev.metaKey,
						button: 0,
						buttons: 0,
						view: window,
						composed: true,
						bubbles: true,
						cancelable: true,
					});

					mev.$dnd_trusted = true;

					ev.target.dispatchEvent(mev);
				}
			}

			emulateEvents();
		}

		if (isTouch) {
			me.monDom(me.containerEl, 'touchstart', onTouch);
			me.monDom(me.containerEl, 'touchmove', onTouch);
			me.monDom(me.containerEl, 'touchend', onTouch);
			me.monDom(me.containerEl, 'touchcancel', onTouch);
			me.monDom(me.containerEl, 'contextmenu', ev => {
				ev.preventDefault();
			});
		}

		me.monDom(me.containerEl, 'mousedown', ev => {
			if (isTouch && !ev.$dnd_trusted) {
				return;
			}

			// don't initiate dragging and keep standard mouse behavior inside editable elements
			if (me.suppressInEditableElements && me.isEditableElement(ev.target)) {
				return;
			}

			// check start filter and don't initiate dragging if it does not pass
			if (!me.checkStartFilter(ev)) {
				return;
			}

			// blur currently active editable element 
			// (because DnDSupport will cancel default action, meaning that no standard blurring will occur)
			if (me.blurEditableElements) {
				var activeEl = document.activeElement;
				if (activeEl && me.containerEl.contains(activeEl) && me.isEditableElement(activeEl)) {
					activeEl.blur();
				}
			}

			// prevents various browser actions such as autoscrolling, text selection, etc.
			ev.preventDefault();

			// except that we want to lose focus correctly, on click
			if (document.activeElement) {
				document.activeElement.blur();
			}

			pressed = true;
			me.dragging = false;
			me.isAutoScrolling = false; 

			me.lastDownEvent = ev;
			me.lastDownTarget = ev.target;
			me.lastDownTargetOffset = {
				x: ev.offsetX,
				y: ev.offsetY
			};
		});

		me.monDom(document, 'click', ev => {
			// clicks are suppressed while dragging
			if (me.dragging) {
				ev.stopPropagation();
			}
		}, me, true);

		me.monDom(document, 'mousemove', ev => {
			if (isTouch && !ev.$dnd_trusted) {
				return;
			}

			// ignore mouse movement without pressed button
			if (!pressed) {
				return;
			}

			// mousedown/mouseup occasionally does not fire under high load activity, so if
			// we see movement without pressed button, correct out state data to "pressed: false"
			// (not supported on Safari where mousemove events do not contain information about buttons)
			if (ev.buttons === 0) {
				pressed = false;
				return;
			}

			if (!me.dragging) {
				// initial movement still too small - do not consider as dragging yet
				if (Math.max(Math.abs(me.lastDownEvent.clientX - ev.clientX), Math.abs(me.lastDownEvent.clientY - ev.clientY)) < me.initialDistance) {
					return;
				}

				// initial movement threshold was reached - start dragging
				else {
					me.dragging = true;
					me.fire('dragstart', ev);
					me.prepareAutoScroll();
				}
			}

			else {
				me.updateDragImage(ev);
				me.applyAutoScroll(ev);
				me.fire('drag', ev);
			}

			me.lastMoveEvent = ev;
			ev.preventDefault();
		});

		me.monDom(document, 'mouseup', ev => { 
			if (isTouch && !ev.$dnd_trusted) {
				return;
			}

			if (me.dragging) {
				// stop dragging after short timeout in order to correctly suppress
				// the 'click' event that will follow this 'mouseup'
				setTimeout(() => {
					me.dragging = false;
					me.destroyDragImage();
					me.fire('dragend', ev);
				}, 10);

				ev.preventDefault();
			}

			pressed = false;
		});
	},

	/**
	 * @private
	 * Applies and checks start filter (if configured)
	 * 
	 * @return {boolean} True if filter is passed (or not configured) 
	 */
	checkStartFilter(ev) {
		var filter = this.startFilter;

		if (filter) {
			if (typeof filter === 'string') {
				return ev.target.matches(filter);
			}

			if (typeof filter === 'function') {
				return filter(ev);
			}
		}

		return true;
	},

	/**
	 * @private
	 * Tests if given element is editable
	 * 
	 * @return {boolean}
	 */
	isEditableElement(el) {
		return el.matches('input, textarea, [contenteditable=true]');
	},

	/**
	 * @private
	 * Precalculates client area offsets of autoscroll areas for simple checks in mousemove handler 
	 */
	prepareAutoScroll() {
		if (!this.autoScroll) {
			return;
		}

		if (!this.autoScrollEl) {
			this.autoScrollEl = this.containerEl; 
		}

		var limits = this.autoScrollLimits, 
			rect = this.autoScrollEl.getBoundingClientRect();

		this.autoScrollAreas = {
			left: rect.left + limits.left,
			right: rect.right - limits.right,
			top: rect.top + limits.top,
			bottom: rect.bottom - limits.bottom
		};
	},

	/**
	 * @private
	 * Applies autoscroll (if configured)
	 */
	applyAutoScroll(ev) {
		if (!this.autoScroll || !this.dragging || this.isAutoScrolling) {
			return;
		}

		var me = this,
			scrollEl = me.autoScrollEl,
			areas = me.autoScrollAreas,
			distance = me.autoScrollDistance,
			update = null;

		if (ev.clientX < areas.left) {
			update = {left: scrollEl.scrollLeft - distance};
		}
		if (ev.clientX > areas.right) {
			update = {left: scrollEl.scrollLeft + distance};
		}
		if (ev.clientY < areas.top) {
			update = {top: scrollEl.scrollTop - distance};
		}
		if (ev.clientY > areas.bottom) {
			update = {top: scrollEl.scrollTop + distance};
		}

		if (update) {
			me.isAutoScrolling = true;

			scrollEl.scrollTo({
				...update,
				behavior: 'smooth',
			});

			setTimeout(() => {
				me.isAutoScrolling = false;

				// raise drag event to update UI with new scroll position
				me.fire('drag', me.lastMoveEvent);

				// if the last mouse position was still inside autoscroll area, immediately start another scrolling
				// (this makes repeated autoscrolls nicely smooth)
				me.applyAutoScroll(me.lastMoveEvent);
			}, 500);
		}
	},

	/**
	 * Sets drag image for current dragging.
	 * 
	 * The image will be inserted into containerEl and automatically moved around with mouse pointer while dragging.
	 * 
	 * The image will be automatically destroyed when dragging ends. 
	 * 
	 * @param el {DOMElement} Element that will be used as drag image
	 * @param off {object} Object with initial x and y position of the element relative to the container
	 */
	setDragImage(el, off) {
		this.dragImageEl = el;
		this.dragImageOff = off;

		Object.assign(el.style, {
			position: 'absolute',
			left: off.x + 'px',
			top: off.y + 'px'
		});

		this.containerEl.appendChild(el);
	},

	/**
	 * Sets drag image to a clone of an existing element (typically the element that is been dragged).
	 * 
	 * @param el {DOMElement} Element that shall be cloned to create the drag image
	 * @param customizer {function} Optional custom function that will be called with the cloned element in order to customize it
	 */
	setDragElement(el, customizer) {
		var clone = el.cloneNode(true),
			elRect = el.getBoundingClientRect(),
			containerRect = this.containerEl.getBoundingClientRect();

		Object.assign(clone.style, {
			width: elRect.width + 'px',
			height: elRect.height + 'px',
			'box-sizing': 'border-box',
			'pointer-events': 'none', 
			'min-width': '',
			'max-width': ''
		});

		if (customizer) {
			customizer(clone);
		}

		this.setDragImage(clone, {
			x: elRect.left - containerRect.left,
			y: elRect.top - containerRect.top
		});
	},

	/**
	 * Removes drag image
	 */
	destroyDragImage() {
		if (!this.dragImageEl) {
			return;
		}

		this.dragImageEl.remove();
		delete this.dragImageEl;
	},

	/**
	 * @private 
	 */
	updateDragImage(ev) {
		if (!this.dragImageEl) {
			return;
		}

		var off = this.dragImageOff,
			last = this.lastMoveEvent;

		off.x += ev.pageX - last.pageX; 
		off.y += ev.pageY - last.pageY;

		Object.assign(this.dragImageEl.style, {
			left: off.x + 'px',
			top: off.y + 'px'
		});
	}
});

// }}}
